import { useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import type { Status } from '../../../utils/panorama/panoramaPeopleRows';
import { STATUS_ORDER, STATUS_META } from '../vacationOverviewPage.definitions';
import { EmptyChart } from './DashboardCard';
import { useCompactChart } from './useCompactChart';

interface DeadlineStatusDonutProps {
	statusCounts: Record<Status | 'all', number>;
	onStatusClick?: (status: Status) => void;
}

export function DeadlineStatusDonut({ statusCounts, onStatusClick }: DeadlineStatusDonutProps) {
	const total = statusCounts.all;
	const { isCompact } = useCompactChart();
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

	if (total === 0) {
		return <EmptyChart message="Sem saldo de férias calculado para a base atual." />;
	}

	const data = (STATUS_ORDER as Status[])
		.map(status => ({
			status,
			name: STATUS_META[status].label,
			value: statusCounts[status] ?? 0,
			color: STATUS_META[status].color,
		}))
		.filter(d => d.value > 0);

	const hoveredEntry = hoveredIndex !== null ? data[hoveredIndex] : null;

	return (
		<div className="flex flex-col gap-4">
			<div className="relative mx-auto h-44 w-44">
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={data}
							dataKey="value"
							nameKey="name"
							innerRadius={56}
							outerRadius={84}
							paddingAngle={2}
							stroke="#ffffff"
							strokeWidth={2}
							onMouseEnter={(_, index) => !isCompact && setHoveredIndex(index)}
							onMouseLeave={() => !isCompact && setHoveredIndex(null)}
							onClick={(entry) => onStatusClick?.(entry.status)}
							cursor={onStatusClick ? 'pointer' : 'default'}
						>
							{data.map(entry => (
								<Cell key={entry.status} fill={entry.color} />
							))}
						</Pie>
					</PieChart>
				</ResponsiveContainer>
				<div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
					<span className="text-2xl font-bold text-raro-gray-900 font-montserrat">{total}</span>
					<span className="text-[11px] text-raro-gray-500">pessoas</span>
				</div>

				{hoveredEntry && !isCompact && (
					<div className="pointer-events-none absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-20 whitespace-nowrap rounded-lg border border-raro-gray-200 bg-white p-2 text-xs shadow-lg">
						<p className="font-semibold text-raro-gray-900">{hoveredEntry.name}</p>
						<p className="text-raro-gray-500">{hoveredEntry.value} pessoa(s)</p>
					</div>
				)}
			</div>

			<ul className="space-y-1.5">
				{data.map(entry => (
					<li
						key={entry.status}
						className="flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1 text-xs transition hover:bg-raro-gray-100"
						onClick={() => onStatusClick?.(entry.status)}
					>
						<span className="flex min-w-0 items-center gap-2 text-raro-gray-700">
							<span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
							<span className="truncate">{entry.name}</span>
						</span>
						<span className="shrink-0 font-semibold text-raro-gray-900">
							{entry.value} <span className="font-normal text-raro-gray-500">({Math.round((entry.value / total) * 100)}%)</span>
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}
